import React from 'react';
import { theme } from '../theme';

// <purpose>The shared LOOK of the "Texte & Vokabular" tab's two editing sections. Extracted from
// AntwortTexte when the vocabulary section joined it, so the two cannot drift into two dialects of
// the same card: one button voice, one feedback bubble, one pill, one reserved error zone.</purpose>
// <dependencies>theme.ts (colours/spacing); index.css (the stx-fadeIn keyframe).</dependencies>
// <constraints>Layout-Stability rule: everything that appears and disappears here does so INSIDE a
// pre-allocated box whose own footprint never changes — the error zone reserves its height whether
// or not it holds a bubble, and the badge row reserves its line whether or not a pill renders.
// Nothing in this file may collapse when its content is absent.</constraints>

export function btnStyle(kind: 'primary' | 'ghost' | 'danger'): React.CSSProperties {
  const base: React.CSSProperties = {
    padding: '7px 14px', borderRadius: 6, fontSize: 13, cursor: 'pointer',
    border: '1px solid transparent', fontWeight: 500,
  };
  if (kind === 'primary') return { ...base, background: theme.colors.navy, color: theme.colors.white };
  if (kind === 'danger') return { ...base, background: '#fef2f2', color: theme.colors.error };
  return { ...base, background: theme.colors.bgLight, color: theme.colors.navyMid };
}

export function feedbackStyle(isError: boolean): React.CSSProperties {
  return {
    padding: '8px 12px', borderRadius: 6, fontSize: 13,
    background: isError ? '#fbe9e9' : '#e9f6ec', color: isError ? '#b02a2a' : '#1f7a3d',
  };
}

// Same pill shape as EinstellungenSeite's badgeStyle(isAdmin) — one "badge-class element" idiom
// app-wide rather than a bespoke look per section. 'override' = this row carries a customization
// the tenant made; 'frozen' = BE-locked, view-only.
export function pillStyle(kind: 'override' | 'frozen'): React.CSSProperties {
  return {
    fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 10,
    color: kind === 'override' ? theme.colors.white : theme.colors.navyMid,
    backgroundColor: kind === 'override' ? theme.colors.navy : theme.colors.bgLight,
  };
}

// Inline, per-row save/reset/rollback error — same red as feedbackStyle(true) for visual
// continuity. Sits in a fixed-height reserved zone below the action row (in normal flow, never
// absolutely positioned over the gap): at this font-size a wrapping 2-line message needs more room
// than the inter-card gap has, and an overlay would spill into the next card's label.
function inlineFieldErrorStyle(): React.CSSProperties {
  return {
    padding: '6px 10px', borderRadius: 6, fontSize: 12, lineHeight: 1.4,
    background: '#fbe9e9', color: '#b02a2a',
    animation: 'stx-fadeIn 0.2s ease',
  };
}

// The zone renders unconditionally at its full reserved height; only the bubble inside it
// mounts/fades. A row with no failure path (a frozen one) simply never populates it and still
// occupies the same footprint as every other row.
export const InlineErrorZone: React.FC<{ rowKey: string; message?: string }> = ({ rowKey, message }) => (
  <div data-testid={`error-zone-${rowKey}`} style={{ minHeight: 48 }}>
    {message && <div style={inlineFieldErrorStyle()}>{message}</div>}
  </div>
);

// The one German sentence both sections use for a lost update — same structure, same recovery
// instruction, only the subject noun differs ("Der Text" for reply prose, "Die Bezeichnung" for a
// classification wording). A single flattened noun would have made both sentences vaguer than the
// thing they describe; sharing the SENTENCE while parameterising its subject keeps the two
// sections one voice without that cost.
export function staleConflictNotice(subjectDe: string): string {
  return `${subjectDe} wurde zwischenzeitlich geändert — neu geladen, bitte erneut prüfen.`;
}
